import { useEffect, useState } from 'react'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Tag,
    Space,
    message,
    Popconfirm,
    Tabs,
    Switch,
    Steps,
    Avatar,
    Tooltip,
} from 'antd'
import {
    UserOutlined,
    CloudServerOutlined,
    MessageOutlined,
    TeamOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    QuestionCircleOutlined,
    GlobalOutlined,
    MobileOutlined,
} from '@ant-design/icons'
import { statsApi, operatorsApi, accountsApi, proxiesApi } from '../services/api'

interface DashboardStats {
    total_accounts: number
    active_accounts: number
    banned_accounts: number
    total_operators: number
    online_operators: number
    total_messages: number
    messages_today: number
    total_contacts: number
}

export default function Admin() {
    return (
        <Routes>
            <Route path="/" element={<AdminDashboard />} />
            <Route path="/operators" element={<OperatorsPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/proxies" element={<ProxiesPage />} />
        </Routes>
    )
}

function AdminDashboard() {
    const [stats, setStats] = useState<DashboardStats | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        fetchStats()
    }, [])

    const fetchStats = async () => {
        try {
            const response = await statsApi.getDashboard()
            setStats(response.data)
        } catch (error) {
            message.error('Ошибка загрузки статистики')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div style={{ padding: 24 }}>
            <h1 style={{ color: 'white', marginBottom: 24 }}>Dashboard</h1>

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                    <Card className="dashboard-card" loading={loading}>
                        <Statistic
                            title={<span style={{ color: '#888' }}>TG Аккаунты</span>}
                            value={stats?.total_accounts || 0}
                            prefix={<CloudServerOutlined style={{ color: '#1890ff' }} />}
                            suffix={
                                <span style={{ fontSize: 14, color: '#52c41a' }}>
                                    {stats?.active_accounts || 0} активных
                                </span>
                            }
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className="dashboard-card" loading={loading}>
                        <Statistic
                            title={<span style={{ color: '#888' }}>Операторы</span>}
                            value={stats?.total_operators || 0}
                            prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
                            suffix={
                                <span style={{ fontSize: 14, color: '#52c41a' }}>
                                    {stats?.online_operators || 0} онлайн
                                </span>
                            }
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className="dashboard-card" loading={loading}>
                        <Statistic
                            title={<span style={{ color: '#888' }}>Сообщений сегодня</span>}
                            value={stats?.messages_today || 0}
                            prefix={<MessageOutlined style={{ color: '#faad14' }} />}
                            suffix={
                                <span style={{ fontSize: 14, color: '#888' }}>
                                    всего {stats?.total_messages || 0}
                                </span>
                            }
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <Card className="dashboard-card" loading={loading}>
                        <Statistic
                            title={<span style={{ color: '#888' }}>Контакты</span>}
                            value={stats?.total_contacts || 0}
                            prefix={<UserOutlined style={{ color: '#13c2c2' }} />}
                        />
                    </Card>
                </Col>
            </Row>

            {stats?.banned_accounts && stats.banned_accounts > 0 && (
                <Card
                    className="dashboard-card"
                    style={{ marginTop: 16, borderColor: '#ff4d4f' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <ExclamationCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
                        <div>
                            <div style={{ color: '#ff4d4f', fontWeight: 500 }}>
                                {stats.banned_accounts} аккаунтов заблокировано
                            </div>
                            <div style={{ color: '#888', fontSize: 13 }}>
                                Проверьте прокси и замените заблокированные аккаунты
                            </div>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    )
}

// ... imports
import { useAuthStore } from '../store/authStore'
import { InternalChatDrawer } from '../components/InternalChatDrawer'
import {
    // ... existing icons
    WechatOutlined, // Add this icon or similar
} from '@ant-design/icons'

// ...

function OperatorsPage() {
    const { user } = useAuthStore()
    const [operators, setOperators] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingOperator, setEditingOperator] = useState<any>(null)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [form] = Form.useForm()
    const [editForm] = Form.useForm()

    // Chat states
    const [chatDrawerOpen, setChatDrawerOpen] = useState(false)
    const [selectedUserForChat, setSelectedUserForChat] = useState<any>(null)

    useEffect(() => {
        fetchOperators()
    }, [])

    const fetchOperators = async () => {
        try {
            const response = await operatorsApi.getAll()
            setOperators(response.data)
        } catch (error) {
            message.error('Ошибка загрузки пользователей')
        } finally {
            setLoading(false)
        }
    }

    const handleCreate = async (values: any) => {
        try {
            await operatorsApi.create(values)
            message.success('Пользователь создан')
            setModalOpen(false)
            form.resetFields()
            fetchOperators()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка создания')
        }
    }

    const handleDelete = async (id: number) => {
        try {
            await operatorsApi.delete(id)
            message.success('Пользователь удалён')
            fetchOperators()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка удаления')
        }
    }

    const handleEdit = (record: any) => {
        setEditingOperator(record)
        editForm.setFieldsValue({
            username: record.username,
            full_name: record.full_name,
            role: record.role,
            is_active: record.is_active,
        })
        setEditModalOpen(true)
    }

    const handleUpdate = async (values: any) => {
        try {
            // Remove empty password to avoid overwriting
            const dataToUpdate = { ...values }
            if (!dataToUpdate.password) {
                delete dataToUpdate.password
            }

            await operatorsApi.update(editingOperator.id, dataToUpdate)
            message.success('Пользователь обновлён')
            setEditModalOpen(false)
            editForm.resetFields()
            fetchOperators()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка обновления')
        }
    }

    const handleOpenChat = (record: any) => {
        setSelectedUserForChat(record)
        setChatDrawerOpen(true)
    }

    const isTeamLead = user?.role === 'teamlead'

    const columns = [
        {
            title: '',
            key: 'avatar',
            width: 60,
            render: (_: any, record: any) => (
                <Avatar
                    src={record.avatar_url}
                    style={{ backgroundColor: record.role === 'teamlead' ? '#f56a00' : '#1890ff' }}
                >
                    {(record.full_name || record.username || 'U')[0].toUpperCase()}
                </Avatar>
            ),
        },
        {
            title: 'Логин',
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: 'Имя',
            dataIndex: 'full_name',
            key: 'full_name',
        },
        {
            title: 'Роль',
            dataIndex: 'role',
            key: 'role',
            render: (role: string) => (
                <Tag color={role === 'teamlead' ? 'gold' : 'default'}>
                    {role === 'teamlead' ? 'Тимлид' : 'Оператор'}
                </Tag>
            ),
        },
    ]

    // Columns visible only for Team Leads
    if (isTeamLead) {
        columns.push(
            {
                title: 'Статус',
                dataIndex: 'is_active',
                key: 'is_active',
                render: (active: boolean) => (
                    <Tag color={active ? 'success' : 'error'}>
                        {active ? 'Активен' : 'Отключен'}
                    </Tag>
                ),
            } as any,
            {
                title: 'Аккаунты',
                dataIndex: 'accounts_count',
                key: 'accounts_count',
            } as any,
            {
                title: 'Сообщ. сегодня',
                dataIndex: 'messages_today',
                key: 'messages_today',
            } as any
        )
    }

    // Actions column (Visible to everyone for chat, but Edit/Delete are restricted)
    columns.push({
        title: 'Действия',
        key: 'actions',
        render: (_: any, record: any) => (
            <Space>
                {record.id !== user?.id && (
                    <Tooltip title="Написать сообщение">
                        <Button
                            type="text"
                            icon={<MessageOutlined style={{ color: '#1890ff' }} />}
                            onClick={() => handleOpenChat(record)}
                        />
                    </Tooltip>
                )}

                {isTeamLead && (
                    <>
                        <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                        <Popconfirm
                            title="Удалить пользователя?"
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Popconfirm>
                    </>
                )}
            </Space>
        ),
    } as any)

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h1 style={{ color: 'white', margin: 0 }}>Пользователи</h1>
                {isTeamLead && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setModalOpen(true)}
                    >
                        Добавить
                    </Button>
                )}
            </div>

            <Table
                dataSource={operators}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title="Новый пользователь"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
            >
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        name="username"
                        label="Логин"
                        rules={[{ required: true, message: 'Введите логин' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Пароль"
                        rules={[{ required: true, message: 'Введите пароль' }]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item name="full_name" label="Имя">
                        <Input />
                    </Form.Item>
                    <Form.Item name="role" label="Роль" initialValue="operator">
                        <Select>
                            <Select.Option value="operator">Оператор</Select.Option>
                            <Select.Option value="teamlead">Тимлид</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Operator Modal */}
            <Modal
                title="Редактировать пользователя"
                open={editModalOpen}
                onCancel={() => setEditModalOpen(false)}
                onOk={() => editForm.submit()}
            >
                <Form form={editForm} onFinish={handleUpdate} layout="vertical">
                    <Form.Item
                        name="username"
                        label="Логин"
                        rules={[{ required: true, message: 'Введите логин' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Новый пароль"
                        help="Оставьте пустым, если не хотите менять пароль"
                    >
                        <Input.Password placeholder="Новый пароль" />
                    </Form.Item>
                    <Form.Item name="full_name" label="Имя">
                        <Input />
                    </Form.Item>
                    <Form.Item name="role" label="Роль">
                        <Select>
                            <Select.Option value="operator">Оператор</Select.Option>
                            <Select.Option value="teamlead">Тимлид</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="is_active" label="Активен" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            <InternalChatDrawer
                open={chatDrawerOpen}
                onClose={() => setChatDrawerOpen(false)}
                recipientId={selectedUserForChat?.id}
                recipientName={selectedUserForChat?.full_name || selectedUserForChat?.username}
                recipientRole={selectedUserForChat?.role}
            />
        </div>
    )
}

function AccountsPage() {
    const [accounts, setAccounts] = useState<any[]>([])
    const [operators, setOperators] = useState<any[]>([])
    const [proxies, setProxies] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [modalOpen, setModalOpen] = useState(false)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [tdataModalOpen, setTdataModalOpen] = useState(false)
    const [newTdataModalOpen, setNewTdataModalOpen] = useState(false)
    const [selectedAccount, setSelectedAccount] = useState<any>(null)
    const [uploading, setUploading] = useState(false)
    const [refreshingStatuses, setRefreshingStatuses] = useState(false)
    const [form] = Form.useForm()
    const [editForm] = Form.useForm()
    const { user } = useAuthStore()
    const isTeamLead = user?.role === 'teamlead'

    // QR authorization flow states
    const [authModalOpen, setAuthModalOpen] = useState(false)
    const [authStep, setAuthStep] = useState<'qr' | '2fa'>('qr')
    const [authLoading, setAuthLoading] = useState(false)
    const [authAccountId, setAuthAccountId] = useState<number | null>(null)
    const [qrUrl, setQrUrl] = useState<string>('')
    const [qrPollingRef] = useState<{ current: ReturnType<typeof setInterval> | null }>({ current: null })

    // Phone authorization flow states
    const [phoneAuthModalOpen, setPhoneAuthModalOpen] = useState(false)
    const [phoneAuthStep, setPhoneAuthStep] = useState<'phone_input' | 'verify_code' | '2fa'>('phone_input')
    const [phoneAuthPhone, setPhoneAuthPhone] = useState('')
    const [phoneAuthHash, setPhoneAuthHash] = useState('')
    const [phoneAuthAccountId, setPhoneAuthAccountId] = useState<number | null>(null)

    // Proxy Check State
    const [proxyCheckResult, setProxyCheckResult] = useState<any>(null)
    const [proxyCheckModalOpen, setProxyCheckModalOpen] = useState(false)

    useEffect(() => {
        fetchAccounts()
        fetchOperators()
        fetchProxies()
        
        // Setup WebSocket for real-time account updates
        const token = localStorage.getItem('token')
        if (!token) return
        
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const wsUrl = `${wsProtocol}//${window.location.host}/ws?token=${token}`
        
        let ws: WebSocket | null = null
        let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
        let reconnectDelay = 1000
        let isClosed = false

        const connect = () => {
            if (isClosed) return
            ws = new WebSocket(wsUrl)
            ws.onopen = () => { reconnectDelay = 1000 }
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    if (data.type === 'account_update' && data.account) {
                        setAccounts(prev => prev.map(acc => 
                            acc.id === data.account.id ? { ...acc, ...data.account } : acc
                        ))
                    }
                } catch (e) {
                    console.error('WebSocket parse error:', e)
                }
            }
            ws.onclose = () => {
                if (!isClosed) {
                    reconnectTimeout = setTimeout(connect, reconnectDelay)
                    reconnectDelay = Math.min(reconnectDelay * 1.5, 10000)
                }
            }
        }
        
        connect()
        
        return () => {
            isClosed = true
            if (reconnectTimeout) clearTimeout(reconnectTimeout)
            if (ws) ws.close()
        }
    }, [])

    const fetchAccounts = async () => {
        try {
            const response = await accountsApi.getAll(0, 500, true)
            setAccounts(response.data)
        } catch (error) {
            message.error('Ошибка загрузки аккаунтов')
        } finally {
            setLoading(false)
        }
    }

    const handleRefreshStatuses = async () => {
        setRefreshingStatuses(true)
        try {
            const response = await accountsApi.refreshStatuses()
            message.info(`Проверка ${response.data.accounts_count} аккаунтов запущена`)
            window.setTimeout(fetchAccounts, 5000)
            window.setTimeout(fetchAccounts, 15000)
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка актуализации статусов')
        } finally {
            window.setTimeout(() => setRefreshingStatuses(false), 5000)
        }
    }

    const fetchOperators = async () => {
        try {
            const response = await operatorsApi.getAll()
            setOperators(response.data)
        } catch (error) {
            console.error('Error fetching operators:', error)
        }
    }

    const fetchProxies = async () => {
        try {
            const response = await proxiesApi.getAll()
            setProxies(response.data)
        } catch (error) {
            console.error('Error fetching proxies:', error)
        }
    }

    const handleCreate = async (values: any) => {
        try {
            await accountsApi.create(values)
            message.success('Аккаунт создан')
            setModalOpen(false)
            form.resetFields()
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка создания')
        }
    }

    // QR Authorization Flow Handlers
    const handleStartQrAuth = async (values?: { proxy_id?: number; assigned_operator_id?: number }) => {
        setAuthLoading(true)
        try {
            const response = await accountsApi.startQrLogin(values?.proxy_id, values?.assigned_operator_id)
            setAuthAccountId(response.data.account_id)
            setQrUrl(response.data.qr_url)
            setAuthStep('qr')

            // Start polling for QR scan
            startQrPolling(response.data.account_id)
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка генерации QR')
        } finally {
            setAuthLoading(false)
        }
    }

    const startQrPolling = (accountId: number) => {
        if (qrPollingRef.current) clearInterval(qrPollingRef.current)

        qrPollingRef.current = setInterval(async () => {
            try {
                const response = await accountsApi.checkQrLogin(accountId)
                const { status: loginStatus } = response.data

                if (loginStatus === 'success') {
                    stopQrPolling()
                    message.success('Авторизация успешна!')
                    handleAuthComplete()
                } else if (loginStatus === '2fa_required') {
                    stopQrPolling()
                    setAuthStep('2fa')
                    message.info('Требуется пароль 2FA')
                } else if (loginStatus === 'expired') {
                    stopQrPolling()
                    message.warning('QR код истёк. Генерируем новый...')
                    handleStartQrAuth()
                } else if (loginStatus === 'waiting' && response.data.qr_url) {
                    setQrUrl(response.data.qr_url)
                }
            } catch (error) {
                console.error('QR poll error:', error)
            }
        }, 3000)
    }

    const stopQrPolling = () => {
        if (qrPollingRef.current) {
            clearInterval(qrPollingRef.current)
            qrPollingRef.current = null
        }
    }

    const handleVerify2fa = async (password: string) => {
        if (!authAccountId) return
        setAuthLoading(true)
        try {
            await accountsApi.qr2fa(authAccountId, password)
            message.success('Авторизация успешна!')
            handleAuthComplete()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Неверный пароль 2FA')
        } finally {
            setAuthLoading(false)
        }
    }

    const handleAuthComplete = () => {
        stopQrPolling()
        setAuthModalOpen(false)
        setAuthStep('qr')
        setAuthAccountId(null)
        setQrUrl('')
        form.resetFields()
        fetchAccounts()
    }

    const handleAuthCancel = () => {
        stopQrPolling()
        setAuthModalOpen(false)
        setAuthStep('qr')
        setAuthAccountId(null)
        setQrUrl('')
        form.resetFields()
    }

    // Phone Authorization Handlers
    const handleCreateAndSendCode = async (values: any) => {
        setAuthLoading(true)
        try {
            // Create account first
            const createResponse = await accountsApi.create(values)
            const newAccountId = createResponse.data.id
            setPhoneAuthAccountId(newAccountId)
            setPhoneAuthPhone(values.phone)

            // Immediately send code
            const sendResponse = await accountsApi.sendCode(newAccountId)
            setPhoneAuthHash(sendResponse.data.phone_code_hash)
            setPhoneAuthStep('verify_code')
            message.success('Код отправлен')
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка создания аккаунта или отправки кода')
        } finally {
            setAuthLoading(false)
        }
    }

    const handleResendPhoneCode = async () => {
        if (!phoneAuthAccountId || !phoneAuthHash) return
        setAuthLoading(true)
        try {
            const response = await accountsApi.resendCode(phoneAuthAccountId, phoneAuthHash)
            setPhoneAuthHash(response.data.phone_code_hash)
            message.success('Код отправлен повторно (по SMS)')
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка повторной отправки кода')
        } finally {
            setAuthLoading(false)
        }
    }

    const handleVerifyPhoneCode = async (values: any) => {
        if (!phoneAuthAccountId) return
        setAuthLoading(true)
        try {
            const response = await accountsApi.verifyCode(phoneAuthAccountId, values.code, phoneAuthHash)
            if (response.data.status === '2fa_required') {
                setPhoneAuthStep('2fa')
                message.info('Требуется пароль 2FA')
            } else {
                message.success('Авторизация успешна!')
                handlePhoneAuthComplete()
            }
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Неверный код')
        } finally {
            setAuthLoading(false)
        }
    }

    const handleVerifyPhone2fa = async (values: any) => {
        if (!phoneAuthAccountId) return
        setAuthLoading(true)
        try {
            await accountsApi.verify2fa(phoneAuthAccountId, values.password)
            message.success('Авторизация успешна!')
            handlePhoneAuthComplete()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Неверный пароль 2FA')
        } finally {
            setAuthLoading(false)
        }
    }

    const handlePhoneAuthComplete = () => {
        setPhoneAuthModalOpen(false)
        setPhoneAuthStep('phone_input')
        setPhoneAuthAccountId(null)
        setPhoneAuthHash('')
        setPhoneAuthPhone('')
        fetchAccounts()
    }

    const handlePhoneAuthCancel = () => {
        setPhoneAuthModalOpen(false)
        setPhoneAuthStep('phone_input')
        setPhoneAuthAccountId(null)
        setPhoneAuthHash('')
        setPhoneAuthPhone('')
    }

    const handleEdit = async (values: any) => {
        if (!selectedAccount) return
        try {
            await accountsApi.update(selectedAccount.id, values)
            message.success('Аккаунт обновлён')
            setEditModalOpen(false)
            editForm.resetFields()
            setSelectedAccount(null)
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка обновления')
        }
    }

    const handleDelete = async (id: number) => {
        try {
            await accountsApi.delete(id)
            message.success('Аккаунт удалён')
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка удаления')
        }
    }

    const handleConnect = async (id: number) => {
        try {
            await accountsApi.connect(id)
            message.success('Подключение запущено')
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка подключения')
        }
    }

    const handleDisconnect = async (id: number) => {
        try {
            await accountsApi.disconnect(id)
            message.success('Аккаунт отключен')
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка отключения')
        }
    }

    const handleImportTdata = async (file: File) => {
        if (!selectedAccount) return
        setUploading(true)
        try {
            await accountsApi.importTdata(selectedAccount.id, file)
            message.success('TDATA загружен')
            setTdataModalOpen(false)
            setSelectedAccount(null)
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка загрузки TDATA')
        } finally {
            setUploading(false)
        }
    }

    const handleCreateFromTdata = async (file: File) => {
        setUploading(true)
        try {
            const response = await accountsApi.createFromTdata(file)
            message.success(`Аккаунт создан: ${response.data.phone || response.data.username}`)
            setNewTdataModalOpen(false)
            fetchAccounts()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка создания из TDATA')
        } finally {
            setUploading(false)
        }
    }

    const [modal, contextHolder] = Modal.useModal();

    const handleCheckProxy = async (id: number) => {
        const hide = message.loading('Проверка прокси...', 0)
        try {
            const response = await accountsApi.checkProxy(id)
            hide()
            setProxyCheckResult(response.data)
            setProxyCheckModalOpen(true)
        } catch (error: any) {
            hide()
            message.error(error.response?.data?.detail || 'Ошибка проверки')
        }
    }

    const openEditModal = (record: any) => {
        setSelectedAccount(record)
        editForm.setFieldsValue({
            proxy_id: record.proxy_id,
            assigned_operator_id: record.assigned_operator_id,
        })
        setEditModalOpen(true)
    }

    const openTdataModal = (record: any) => {
        setSelectedAccount(record)
        setTdataModalOpen(true)
    }

    const getStatusTag = (record: any) => {
        const config: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
            active: { color: record.is_connected ? 'success' : 'warning', icon: record.is_connected ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />, label: record.is_connected ? 'Активен' : 'Нет соединения' },
            inactive: { color: 'default', icon: <CloseCircleOutlined />, label: 'Сессия недоступна' },
            banned: { color: 'error', icon: <CloseCircleOutlined />, label: 'Заблокирован Telegram' },
            limited: { color: 'warning', icon: <ExclamationCircleOutlined />, label: 'Ограничен Telegram' },
            processing: { color: 'processing', icon: <ExclamationCircleOutlined />, label: 'Авторизация' },
        }
        const { color, icon, label } = config[record.status] || { color: 'default', icon: <QuestionCircleOutlined />, label: record.status || 'Неизвестно' }
        const checkedAt = record.status_checked_at
            ? new Date(record.status_checked_at).toLocaleString('ru-RU')
            : 'ещё не проверялся автоматически'
        const tooltip = (
            <div style={{ maxWidth: 360 }}>
                <div>{record.status_reason || 'Причина статуса пока не зафиксирована.'}</div>
                <div style={{ marginTop: 6, opacity: 0.75 }}>Последняя проверка: {checkedAt}</div>
            </div>
        )
        return (
            <Space size={4}>
                <Tag color={color} icon={icon}>{label}</Tag>
                <Tooltip title={tooltip} placement="topRight">
                    <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                </Tooltip>
            </Space>
        )
    }

    // Action buttons visible if user is teamlead OR user is the assigned operator
    const canManageAccount = (record: any) =>
        isTeamLead || record.assigned_operator_id === user?.id

    const columns = [
        {
            title: 'Телефон',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Юзернейм',
            dataIndex: 'username',
            key: 'username',
            render: (text: string) => text ? `@${text}` : '-',
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (_status: string, record: any) => getStatusTag(record),
        },
        {
            title: 'Подключен',
            dataIndex: 'is_connected',
            key: 'is_connected',
            render: (v: boolean) => (
                <Tag color={v ? 'success' : 'default'}>{v ? 'Да' : 'Нет'}</Tag>
            ),
        },
        {
            title: 'Оператор',
            key: 'operator',
            render: (_: any, record: any) =>
                record.assigned_operator?.full_name || record.assigned_operator?.username || '-',
        },
        {
            title: 'Прокси',
            key: 'proxy',
            render: (_: any, record: any) =>
                record.proxy?.name || '-',
        },
        {
            title: 'Чатов',
            dataIndex: 'chats_count',
            key: 'chats_count',
        },
        {
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space size={4}>
                    {canManageAccount(record) && (
                        <>
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => openEditModal(record)}
                                title="Редактировать статус/прокси"
                                style={{
                                    color: '#6366f1',
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    borderRadius: 8,
                                }}
                            />
                            <Button
                                type="text"
                                icon={<CloudServerOutlined />}
                                onClick={() => openTdataModal(record)}
                                title="Загрузить TDATA"
                                style={{
                                    color: '#8b5cf6',
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    borderRadius: 8,
                                }}
                            />
                        </>
                    )}
                    {record.is_connected ? (
                        <Popconfirm
                            title="Отключить аккаунт?"
                            onConfirm={() => handleDisconnect(record.id)}
                            disabled={!canManageAccount(record)}
                        >
                            <Button
                                type="text"
                                icon={<CloseCircleOutlined />}
                                title="Отключить"
                                disabled={!canManageAccount(record)}
                                style={{
                                    color: canManageAccount(record) ? '#f59e0b' : '#d9d9d9',
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    borderRadius: 8,
                                }}
                            />
                        </Popconfirm>
                    ) : (
                        <Button
                            type="text"
                            icon={<CheckCircleOutlined />}
                            onClick={() => handleConnect(record.id)}
                            title="Подключить"
                            disabled={!canManageAccount(record)}
                            style={{
                                color: canManageAccount(record) ? '#10b981' : '#d9d9d9',
                                width: 32,
                                height: 32,
                                padding: 0,
                                borderRadius: 8,
                            }}
                        />
                    )}
                    <Button
                        type="text"
                        icon={<GlobalOutlined />}
                        onClick={() => handleCheckProxy(record.id)}
                        title="Проверить IP прокси"
                        style={{
                            color: '#1890ff',
                            width: 32,
                            height: 32,
                            padding: 0,
                            borderRadius: 8,
                        }}
                    />
                    {isTeamLead && (
                        <Popconfirm
                            title="Удалить аккаунт?"
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <Button
                                type="text"
                                icon={<DeleteOutlined />}
                                title="Удалить"
                                style={{
                                    color: '#ef4444',
                                    width: 32,
                                    height: 32,
                                    padding: 0,
                                    borderRadius: 8,
                                }}
                            />
                        </Popconfirm>
                    )}
                </Space >
            ),
        },
    ]

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h1 style={{ color: 'white', margin: 0 }}>TG Аккаунты</h1>
                <Space>
                    {isTeamLead && (
                        <Button
                            icon={<ReloadOutlined spin={refreshingStatuses} />}
                            onClick={handleRefreshStatuses}
                            loading={refreshingStatuses}
                        >
                            Актуализировать статусы
                        </Button>
                    )}
                    <Button
                        icon={<CloudServerOutlined />}
                        onClick={() => setNewTdataModalOpen(true)}
                    >
                        Загрузить TDATA
                    </Button>
                    <Button
                        icon={<MobileOutlined />}
                        onClick={() => setPhoneAuthModalOpen(true)}
                    >
                        Авторизация по номеру
                    </Button>
                    <Button
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setAuthModalOpen(true)
                            handleStartQrAuth()
                        }}
                    >
                        QR Авторизация
                    </Button>
                </Space>
            </div>

            <Table
                dataSource={accounts}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
            />

            {/* Modal: Create Account */}
            <Modal
                title="Новый TG аккаунт"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
            >
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        name="phone"
                        label="Номер телефона"
                        rules={[{ required: true, message: 'Введите номер телефона' }]}
                    >
                        <Input placeholder="+79001234567" />
                    </Form.Item>
                    <Form.Item name="proxy_id" label="Прокси">
                        <Select placeholder="Выберите прокси" allowClear>
                            {proxies.map((p: any) => (
                                <Select.Option key={p.id} value={p.id}>
                                    {p.name} ({p.host}:{p.port})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="assigned_operator_id" label="Назначить оператору">
                        <Select placeholder="Выберите оператора" allowClear>
                            {operators.map((o: any) => (
                                <Select.Option key={o.id} value={o.id}>
                                    {o.full_name || o.username}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal: Edit Account */}
            <Modal
                title={`Редактирование: ${selectedAccount?.phone || ''}`}
                open={editModalOpen}
                onCancel={() => {
                    setEditModalOpen(false)
                    setSelectedAccount(null)
                }}
                onOk={() => editForm.submit()}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Form.Item name="proxy_id" label="Прокси">
                        <Select placeholder="Выберите прокси" allowClear>
                            {proxies.map((p: any) => (
                                <Select.Option key={p.id} value={p.id}>
                                    {p.name} ({p.host}:{p.port})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="assigned_operator_id" label="Назначить оператору">
                        <Select placeholder="Выберите оператора" allowClear>
                            {operators.map((o: any) => (
                                <Select.Option key={o.id} value={o.id}>
                                    {o.full_name || o.username}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal: Import TDATA */}
            <Modal
                title={`Загрузка TDATA: ${selectedAccount?.phone || ''}`}
                open={tdataModalOpen}
                onCancel={() => {
                    setTdataModalOpen(false)
                    setSelectedAccount(null)
                }}
                footer={null}
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <p style={{ marginBottom: 16, color: '#888' }}>
                        Загрузите ZIP архив с TDATA папкой из Telegram Desktop
                    </p>
                    <input
                        type="file"
                        accept=".zip"
                        onChange={(e) => {
                            if (e.target.files?.[0]) {
                                handleImportTdata(e.target.files[0])
                            }
                        }}
                        disabled={uploading}
                        style={{ marginBottom: 16 }}
                    />
                    {uploading && <p>Загрузка...</p>}
                </div>
            </Modal>

            {/* Modal: Create Account from TDATA */}
            <Modal
                title="Создать аккаунт из TDATA"
                open={newTdataModalOpen}
                onCancel={() => setNewTdataModalOpen(false)}
                footer={null}
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <p style={{ marginBottom: 16, color: '#888' }}>
                        Загрузите ZIP архив с TDATA папкой.<br />
                        Номер телефона и данные аккаунта будут извлечены автоматически.
                    </p>
                    <input
                        type="file"
                        accept=".zip"
                        onChange={(e) => {
                            if (e.target.files?.[0]) {
                                handleCreateFromTdata(e.target.files[0])
                            }
                        }}
                        disabled={uploading}
                        style={{ marginBottom: 16 }}
                    />
                    {uploading && <p>Обработка TDATA... Это может занять несколько секунд.</p>}
                </div>
            </Modal>

            {/* Modal: QR Code Authorization */}
            <Modal
                title="Авторизация через QR код"
                open={authModalOpen}
                onCancel={handleAuthCancel}
                footer={null}
                width={480}
            >
                {authStep === 'qr' && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        {authLoading && !qrUrl ? (
                            <div style={{ padding: 40 }}>
                                <div style={{ fontSize: 16, color: '#888' }}>Генерация QR кода...</div>
                            </div>
                        ) : qrUrl ? (
                            <>
                                <div style={{
                                    background: 'white',
                                    borderRadius: 16,
                                    padding: 16,
                                    display: 'inline-block',
                                    marginBottom: 20
                                }}>
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(qrUrl)}`}
                                        alt="QR Code"
                                        style={{ width: 280, height: 280, display: 'block' }}
                                    />
                                </div>
                                <div style={{ marginBottom: 12 }}>
                                    <p style={{ color: '#10b981', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                                        Отсканируйте QR код в Telegram
                                    </p>
                                    <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>
                                        Откройте Telegram → Настройки → Устройства → Подключить устройство
                                    </p>
                                </div>
                                <div style={{
                                    background: 'rgba(16, 185, 129, 0.1)',
                                    border: '1px solid rgba(16, 185, 129, 0.2)',
                                    borderRadius: 8,
                                    padding: '8px 16px',
                                    display: 'inline-block'
                                }}>
                                    <span style={{ color: '#10b981', fontSize: 13 }}>⏳ Ожидание сканирования...</span>
                                </div>
                            </>
                        ) : null}
                    </div>
                )}

                {authStep === '2fa' && (
                    <Form
                        layout="vertical"
                        onFinish={(values) => handleVerify2fa(values.password)}
                    >
                        <p style={{ marginBottom: 16, color: '#888', textAlign: 'center' }}>
                            QR код отсканирован!<br />
                            На этом аккаунте включена двухфакторная аутентификация.<br />
                            Введите пароль 2FA:
                        </p>
                        <Form.Item
                            name="password"
                            label="Пароль 2FA"
                            rules={[{ required: true, message: 'Введите пароль 2FA' }]}
                        >
                            <Input.Password placeholder="Пароль" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={authLoading} block>
                            Авторизоваться
                        </Button>
                    </Form>
                )}
            </Modal>

            {/* Modal: Phone Authorization */}
            <Modal
                title={phoneAuthStep === 'phone_input' ? 'Авторизация по номеру' : `Авторизация: ${phoneAuthPhone}`}
                open={phoneAuthModalOpen}
                onCancel={handlePhoneAuthCancel}
                footer={null}
                width={480}
            >
                {phoneAuthStep === 'phone_input' && (
                    <Form layout="vertical" onFinish={handleCreateAndSendCode}>
                        <p style={{ color: '#888', marginBottom: 24, textAlign: 'center' }}>
                            Введите номер телефона для добавления нового аккаунта и получения кода подтверждения.
                        </p>
                        <Form.Item
                            name="phone"
                            label="Номер телефона"
                            rules={[{ required: true, message: 'Введите номер телефона' }]}
                        >
                            <Input placeholder="+79001234567" />
                        </Form.Item>
                        <Form.Item name="proxy_id" label="Прокси (необязательно)">
                            <Select placeholder="Выберите прокси" allowClear>
                                {proxies.map((p: any) => (
                                    <Select.Option key={p.id} value={p.id}>
                                        {p.name} ({p.host}:{p.port})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="assigned_operator_id" label="Назначить оператору (необязательно)">
                            <Select placeholder="Выберите оператора" allowClear>
                                {operators.map((o: any) => (
                                    <Select.Option key={o.id} value={o.id}>
                                        {o.full_name || o.username}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={authLoading}
                            block
                        >
                            Создать и отправить код
                        </Button>
                    </Form>
                )}

                {phoneAuthStep === 'verify_code' && (
                    <Form
                        layout="vertical"
                        onFinish={handleVerifyPhoneCode}
                    >
                        <p style={{ marginBottom: 16, color: '#888', textAlign: 'center' }}>
                            Код отправлен на номер <b>{phoneAuthPhone}</b>.<br />
                            Проверьте системные сообщения в приложении Telegram.
                        </p>
                        <Form.Item
                            name="code"
                            label="Код подтверждения"
                            rules={[{ required: true, message: 'Введите код' }]}
                        >
                            <Input placeholder="12345" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={authLoading} block style={{ marginBottom: 12 }}>
                            Подтвердить код
                        </Button>
                        <Button type="default" onClick={handleResendPhoneCode} loading={authLoading} block>
                            Отправить код по SMS (Переотправить)
                        </Button>
                    </Form>
                )}

                {phoneAuthStep === '2fa' && (
                    <Form
                        layout="vertical"
                        onFinish={handleVerifyPhone2fa}
                    >
                        <p style={{ marginBottom: 16, color: '#888', textAlign: 'center' }}>
                            На этом аккаунте включена двухфакторная аутентификация.<br />
                            Введите пароль 2FA:
                        </p>
                        <Form.Item
                            name="password"
                            label="Пароль 2FA"
                            rules={[{ required: true, message: 'Введите пароль' }]}
                        >
                            <Input.Password placeholder="Пароль" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={authLoading} block>
                            Авторизоваться
                        </Button>
                    </Form>
                )}
            </Modal>

            {/* Modal: Proxy Check Result */}
            <Modal
                title={proxyCheckResult?.success ? 'Прокси работает' : 'Ошибка прокси'}
                open={proxyCheckModalOpen}
                onCancel={() => setProxyCheckModalOpen(false)}
                footer={[
                    <Button key="ok" type="primary" onClick={() => setProxyCheckModalOpen(false)}>
                        OK
                    </Button>
                ]}
            >
                {proxyCheckResult && (
                    <div style={{ color: 'white', marginTop: 16 }}>
                        {proxyCheckResult.success ? (
                            <>
                                <p style={{ marginBottom: 8, fontSize: 16 }}>Внешний IP: <b style={{ color: '#52c41a' }}>{proxyCheckResult.ip}</b></p>
                                <p style={{ color: '#94a3b8' }}>Прокси: {proxyCheckResult.proxy}</p>
                            </>
                        ) : (
                            <p style={{ color: '#ff4d4f', fontSize: 16 }}>{proxyCheckResult.message}</p>
                        )}
                    </div>
                )}
            </Modal>

            {contextHolder}
        </div>
    )
}

function ProxiesPage() {
    const [proxies, setProxies] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [proxyCheckResult, setProxyCheckResult] = useState<any>(null)
    const [proxyCheckModalOpen, setProxyCheckModalOpen] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingProxy, setEditingProxy] = useState<any>(null)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [form] = Form.useForm()
    const [editForm] = Form.useForm()
    const { user } = useAuthStore()
    const isTeamLead = user?.role === 'teamlead'

    useEffect(() => {
        fetchProxies()
    }, [])

    const fetchProxies = async () => {
        try {
            const response = await proxiesApi.getAll()
            setProxies(response.data)
        } catch (error) {
            message.error('Ошибка загрузки прокси')
        } finally {
            setLoading(false)
        }
    }

    const handleCreate = async (values: any) => {
        try {
            await proxiesApi.create(values)
            message.success('Прокси добавлен')
            setModalOpen(false)
            form.resetFields()
            fetchProxies()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка создания')
        }
    }

    const handleCheck = async (id: number) => {
        const hide = message.loading('Проверка прокси...', 0)
        try {
            const response = await proxiesApi.check(id)
            hide()
            setProxyCheckResult(response.data)
            setProxyCheckModalOpen(true)
            fetchProxies()
        } catch (error: any) {
            hide()
            message.error(error.response?.data?.detail || 'Ошибка проверки')
        }
    }

    const handleEdit = (record: any) => {
        setEditingProxy(record)
        editForm.setFieldsValue({
            name: record.name,
            type: record.type,
            host: record.host,
            port: record.port,
            username: record.username,
            password: record.password,
            country: record.country,
            is_mobile: record.is_mobile,
            status: record.status,
        })
        setEditModalOpen(true)
    }

    const handleUpdate = async (values: any) => {
        try {
            await proxiesApi.update(editingProxy.id, values)
            message.success('Прокси обновлён')
            setEditModalOpen(false)
            editForm.resetFields()
            fetchProxies()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка обновления')
        }
    }

    const handleDelete = async (id: number) => {
        try {
            await proxiesApi.delete(id)
            message.success('Прокси удалён')
            fetchProxies()
        } catch (error: any) {
            message.error(error.response?.data?.detail || 'Ошибка удаления')
        }
    }

    const columns = [
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => <Tag>{type.toUpperCase()}</Tag>,
        },
        {
            title: 'Адрес',
            key: 'address',
            render: (_: any, record: any) => `${record.host}:${record.port}`,
        },
        {
            title: 'Страна',
            dataIndex: 'country',
            key: 'country',
            render: (country: string) => country || '-',
        },
        {
            title: 'Мобильный',
            dataIndex: 'is_mobile',
            key: 'is_mobile',
            render: (v: boolean) => (v ? <Tag color="success">Да</Tag> : '-'),
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'active' ? 'success' : status === 'error' ? 'error' : 'default'}>
                    {status}
                </Tag>
            ),
        },
    ]

    if (isTeamLead) {
        columns.push({
            title: 'Действия',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        type="text"
                        icon={<ReloadOutlined />}
                        onClick={() => handleCheck(record.id)}
                        title="Проверить"
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        title="Редактировать"
                    />
                    <Popconfirm
                        title="Удалить прокси?"
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} title="Удалить" />
                    </Popconfirm>
                </Space>
            ),
        } as any)
    }

    return (
        <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <h1 style={{ color: 'white', margin: 0 }}>Прокси</h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setModalOpen(true)}
                >
                    Добавить
                </Button>
            </div>

            <Table
                dataSource={proxies}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title="Новый прокси"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
            >
                <Form form={form} onFinish={handleCreate} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Название"
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="Proxy RU Mobile 1" />
                    </Form.Item>
                    <Form.Item name="type" label="Тип" initialValue="socks5">
                        <Select>
                            <Select.Option value="socks5">SOCKS5</Select.Option>
                            <Select.Option value="http">HTTP</Select.Option>
                            <Select.Option value="https">HTTPS</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="host"
                        label="Хост"
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="proxy.example.com" />
                    </Form.Item>
                    <Form.Item
                        name="port"
                        label="Порт"
                        rules={[{ required: true }]}
                    >
                        <Input type="number" placeholder="1080" />
                    </Form.Item>
                    <Form.Item name="username" label="Логин">
                        <Input />
                    </Form.Item>
                    <Form.Item name="password" label="Пароль">
                        <Input.Password />
                    </Form.Item>
                    <Form.Item name="country" label="Страна">
                        <Input placeholder="RU" />
                    </Form.Item>
                    <Form.Item name="is_mobile" valuePropName="checked" label="">
                        <Select defaultValue={false}>
                            <Select.Option value={false}>Не мобильный</Select.Option>
                            <Select.Option value={true}>Мобильный</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal: Edit Proxy */}
            <Modal
                title="Редактировать прокси"
                open={editModalOpen}
                onCancel={() => setEditModalOpen(false)}
                onOk={() => editForm.submit()}
            >
                <Form form={editForm} onFinish={handleUpdate} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Название"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="type" label="Тип">
                        <Select>
                            <Select.Option value="socks5">SOCKS5</Select.Option>
                            <Select.Option value="http">HTTP</Select.Option>
                            <Select.Option value="https">HTTPS</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="host"
                        label="Хост"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="port"
                        label="Порт"
                        rules={[{ required: true }]}
                    >
                        <Input type="number" />
                    </Form.Item>
                    <Form.Item name="username" label="Логин">
                        <Input />
                    </Form.Item>
                    <Form.Item name="password" label="Пароль">
                        <Input.Password />
                    </Form.Item>
                    <Form.Item name="country" label="Страна">
                        <Input />
                    </Form.Item>
                    <Form.Item name="is_mobile" valuePropName="checked" label="">
                        <Select>
                            <Select.Option value={false}>Не мобильный</Select.Option>
                            <Select.Option value={true}>Мобильный</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="status" label="Статус">
                        <Select>
                            <Select.Option value="active">Активен</Select.Option>
                            <Select.Option value="error">Ошибка</Select.Option>
                            <Select.Option value="inactive">Неактивен</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal: Proxy Check Result */}
            <Modal
                title={proxyCheckResult?.success ? 'Прокси работает' : 'Ошибка прокси'}
                open={proxyCheckModalOpen}
                onCancel={() => setProxyCheckModalOpen(false)}
                footer={[
                    <Button key="ok" type="primary" onClick={() => setProxyCheckModalOpen(false)}>
                        OK
                    </Button>
                ]}
            >
                {proxyCheckResult && (
                    <div style={{ marginTop: 16 }}>
                        {proxyCheckResult.success ? (
                            <>
                                <p style={{ marginBottom: 8, fontSize: 16 }}>Внешний IP: <b style={{ color: '#52c41a' }}>{proxyCheckResult.ip}</b></p>
                                <p style={{ color: '#94a3b8' }}>Прокси: {proxyCheckResult.proxy}</p>
                            </>
                        ) : (
                            <p style={{ color: '#ff4d4f', fontSize: 16 }}>{proxyCheckResult.message}</p>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    )
}
